import React, { useState, useRef } from 'react';
import { Calendar, Download, Send, Star } from 'lucide-react';
import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf';
import { Button } from './components/ui/Button';
import { Avatar, AvatarImage, AvatarFallback } from './components/ui/Avatar';
import logo from './assets/logos/LogoA/Untitled-1@0.25x.png';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

const variants = {
  green: 'bg-[#28a948] text-white',
  red: 'bg-[#fc0035] text-white',
  amber: 'bg-[#ffae00] text-black',
} as const;
const sizes = {
  sm: 'text-[11px] h-5 px-1.5',
  md: 'text-[12px] h-6 px-2.5',
  lg: 'text-[14px] h-8 px-3',
} as const;

interface BadgeProps {
  children: React.ReactNode;
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
}
const Badge: React.FC<BadgeProps> = ({ children, variant = 'amber', size = 'md' }) => (
  <div className={`inline-flex items-center rounded-full font-medium ${variants[variant]} ${sizes[size]}`}>{children}</div>
);

// Removed custom Button and Avatar; now using shared UI components

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className = '', ...props }, ref) => (
  <textarea ref={ref} className={`min-h-[80px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-none ${className}`} {...props} />
));
Textarea.displayName = 'Textarea';

interface Comment {
  id: string;
  author: string;
  content: string;
  timestamp: string;
  avatar?: string;
}

interface MobileReviewPageProps {
  documentTitle?: string;
  status?: 'open' | 'approved' | 'overdue';
  documentUrl?: string;
  documentType?: 'pdf' | 'image';
  uploadDate?: string;
  deadline?: string;
  reviewerName?: string;
  comments?: Comment[];
  isReviewer?: boolean;
  onApprove?: () => void;
  onRequestChanges?: () => void;
  isRequesting?: boolean;
  requestMessage?: string | null;
  // onBack removed
  onDownload?: () => void;
  onAddComment?: (comment: string) => void;
}
const MobileReviewPage: React.FC<MobileReviewPageProps> = ({
  documentTitle = 'Sample Document Review',
  status = 'open',
  documentUrl = 'https://via.placeholder.com/400x600/f0f0f0/666?text=Document+Preview',
  documentType = 'image',
  uploadDate = '2024-07-22',
  deadline = '2024-07-30',
  reviewerName = 'John Doe',
  comments = [],
  isReviewer = true,
  onApprove = () => {},
  onRequestChanges = () => {},
  isRequesting = false,
  requestMessage = null,
  // onBack prop removed
  onDownload = () => {},
  onAddComment = () => {},
}) => {
  const [newComment, setNewComment] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [numPages, setNumPages] = useState<number>(1);
  const [pageWidth, setPageWidth] = useState<number>(window.innerWidth - 32); // 32px padding

  React.useEffect(() => {
    const handleResize = () => setPageWidth(window.innerWidth - 32);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getStatusVariant = (status: 'open' | 'approved' | 'overdue' | undefined) => {
    switch (status) {
      case 'approved': return 'green';
      case 'overdue': return 'red';
      default: return 'amber';
    }
  };
  return (
    <div className="min-h-screen bg-background p-4 max-w-md mx-auto">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <img src={logo} alt="Approvia Logo" className="w-8 h-8 object-contain" />
          <a href="https://approvia.io" target="_blank" rel="noopener noreferrer">
            <Button variant="default" size="sm" className="flex items-center gap-2">
              <Star className="h-4 w-4 mr-1" />
              Create Your Review
            </Button>
          </a>
        </div>
        <h1 className="text-xl font-bold text-center mb-2 truncate">{documentTitle}</h1>
        <div className="flex justify-center">
          <Badge variant={getStatusVariant(status)} size="sm">{status}</Badge>
        </div>
      </header>
      {/* Document Viewer */}
      <section className="mb-6">
        <div className="w-full bg-gray-100 rounded-lg overflow-x-auto mb-4" style={{ maxWidth: '100vw' }}>
          {documentType === 'image' ? (
            <img src={documentUrl} alt="Document preview" className="w-full h-auto object-contain" style={{ maxHeight: '400px' }} />
          ) : (
            <div className="w-full flex flex-col items-center justify-center">
              <PdfDocument
                file={documentUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={<div className="text-center py-8">Loading PDF...</div>}
                error={<div className="text-center py-8 text-red-500">Failed to load PDF.</div>}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <div key={i} className="flex justify-center my-2">
                    <PdfPage pageNumber={i + 1} width={pageWidth} />
                  </div>
                ))}
              </PdfDocument>
            </div>
          )}
        </div>
        <Button variant="outline" onClick={onDownload} className="w-full">
          <Download size={16} className="mr-2" />Download
        </Button>
      </section>
      {/* Document Info */}
      <section className="mb-6 space-y-2">
        <div className="text-sm text-muted-foreground">Uploaded: {uploadDate}</div>
        <div className="text-sm text-muted-foreground flex items-center"><Calendar size={14} className="mr-1" />Deadline: {deadline}</div>
        {reviewerName && <div className="text-sm text-muted-foreground">Reviewer: {reviewerName}</div>}
      </section>
      {/* Instructions */}
      <section className="mb-6 space-y-2 text-sm text-muted-foreground bg-card border rounded-lg p-4">
        <h2 className="text-base font-semibold text-foreground mb-1">How to review</h2>
        <ul className="list-disc pl-4 space-y-1">
          <li>Tap anywhere on the document to add annotations.</li>
          <li>Scroll down to leave general comments.</li>
          <li>Press “Request Changes” to send feedback or “Approve” when finished.</li>
        </ul>
      </section>

      {/* Comments Section */}
      <section className="mb-6">
        <h2 className="text-lg font-bold mb-4">Comments</h2>
        <div className="space-y-4 mb-4">
          {comments.map((comment) => (
            <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start space-x-3">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={comment.avatar} />
                  <AvatarFallback>{comment.author ? comment.author.split(' ').map(n => n[0]).join('').toUpperCase() : 'R'}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-medium">{comment.author}</span>
                    <span className="text-xs text-muted-foreground">{comment.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-700">{comment.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <Textarea ref={textareaRef} placeholder="Add a comment..." value={newComment} onChange={e => setNewComment(e.target.value)} className="min-h-[80px]" />
          <Button onClick={() => { if (newComment.trim()) { onAddComment(newComment.trim()); setNewComment(''); } }} disabled={!newComment.trim()} className="w-full">
            <Send size={16} className="mr-2" />Send
          </Button>
        </div>
      </section>
      {/* Actions */}
      {isReviewer && status === 'open' && (
        <>
          <div className="mb-6">
            <Button variant="outline" onClick={onRequestChanges} disabled={isRequesting} className="w-full gap-2 h-12 text-base">
              {isRequesting ? 'Sending…' : <>Request Changes</>}
            </Button>
            {requestMessage && <p className="text-center text-xs text-green-700 mt-2">{requestMessage}</p>}
          </div>
          <div className="mb-6">
            <Button variant="default" onClick={onApprove} className="w-full gap-2 h-12 text-base">
              Approve
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileReviewPage; 